/* =========================================================
   Document engines for the compliance viewer.

   The prototype pulled pdf.js and mammoth from a CDN at
   runtime. Here they are real dependencies, code-split so the
   ~1MB of viewer machinery is only fetched when someone
   actually opens a document.
   ========================================================= */
import type { PDFDocumentProxy } from 'pdfjs-dist';

let pdfjsPromise: Promise<typeof import('pdfjs-dist')> | null = null;

export async function loadPdfJs() {
  if (!pdfjsPromise) {
    pdfjsPromise = (async () => {
      const lib = await import('pdfjs-dist');
      // bundled worker — no CDN, works offline
      const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
      lib.GlobalWorkerOptions.workerSrc = workerUrl;
      return lib;
    })().catch((e) => {
      pdfjsPromise = null;
      throw e;
    });
  }
  return pdfjsPromise;
}

export async function openPdf(dataUrl: string): Promise<PDFDocumentProxy> {
  const lib = await loadPdfJs();
  return lib.getDocument({ url: dataUrl }).promise;
}

export async function docxToHtml(dataUrl: string): Promise<string> {
  const [mammoth, arrayBuffer] = await Promise.all([
    import('mammoth').then((m) => (m.default ?? m) as typeof import('mammoth')),
    fetch(dataUrl).then((r) => r.arrayBuffer()),
  ]);
  const result = await mammoth.convertToHtml({ arrayBuffer });
  return result.value || '<p>—</p>';
}
